import { Howl, Howler } from 'howler';
/**
 * WP-REF.8 — how long a stopped sound takes to fade out. A looping bed cut
 * dead mid-cycle clicks; ~150ms reads as the liquid settling instead.
 */
export const STOP_FADE_MS = 150;
/**
 * Module 150 (WP-150.2) — a stopped loop at least this long is unloaded.
 *
 * Web Audio decodes a whole file to PCM on load, and the cache below kept every
 * Howl for the life of the page. Music rotates every puzzle (Module 64), so
 * after ~6 puzzles all six music tracks sat decoded at once: ~70 MB of float32
 * for 374 s of mono audio, held for the rest of the session on a phone
 * (planning/150-performance). Sizing by duration rather than by an id list
 * keeps the adapter channel-agnostic: every music bed is 17–95 s, every SFX
 * (looping ones included) is under 3 s. A track played again is fetched from
 * the browser's HTTP cache and decoded once more; music fades in, so that
 * ~100–200 ms is not heard.
 */
export const RELEASE_LOOP_MIN_SECONDS = 10;
/**
 * Where `{audioId}.mp3` is fetched from. Root-absolute, which is correct for
 * the main site and the Android WebView — both serve the app from the origin
 * root — and wrong everywhere else.
 */
export const DEFAULT_AUDIO_BASE_URL = '/audio/';
/**
 * Keyed by resolved src, not by audio id.
 *
 * Module-scoped so every adapter instance shares one decoded buffer per sound.
 * Keying on the id instead would mean two adapters with different base URLs
 * silently served each other's cached Howls — which for the default base is
 * exactly today's behaviour, and for any other base is a bug waiting for the
 * second adapter to exist.
 */
const _cache = new Map();
/** resolved src -> the Howler sound id of its current looping playback. */
const _looping = new Map();
function getOrCreate(src) {
    if (!_cache.has(src)) {
        _cache.set(src, new Howl({ src: [src] }));
    }
    return _cache.get(src);
}
export class HowlerAdapter {
    constructor(options = {}) {
        const base = options.assetBaseUrl ?? DEFAULT_AUDIO_BASE_URL;
        this._baseUrl = base.endsWith('/') ? base : `${base}/`;
    }
    /** The URL an id resolves to. The id⇄filename contract is Audio_Rules.md's. */
    _src(audioId) {
        return `${this._baseUrl}${audioId}.mp3`;
    }
    /**
     * WP-50.11 (AUD-023) — warm the cues that ride an animation.
     *
     * `getOrCreate` builds the `Howl` on the FIRST PLAY of each id, which
     * `Audio_Rules.md` states as the design ("lazily, on first play"). What it did
     * not state is the consequence, which the audit measured: the gap between the
     * `play()` call and the sound actually starting is 24–83ms for most cues — at
     * or under the threshold where a UI sound reads as attached to its press — and
     * **175ms for `level_complete`**, which exists to ride the confetti burst. On
     * the very first level a player clears, it arrives a sixth of a second after
     * the burst it is supposed to be part of. Every later win is correct, because
     * the file is then cached.
     *
     * Constructing the `Howl` is what does the work: Howler fetches and decodes on
     * construction, so by the time the cue is played the buffer is there. Nothing
     * is played here and no volume is touched.
     *
     * Deliberately NOT every id. Warming all 21 costs 336KB of fetches during the
     * loading screen — trading a compliance metric (time to playable, which
     * CrazyGames measures) for a cosmetic one. The caller passes the short list.
     */
    preload(audioIds) {
        for (const audioId of audioIds)
            getOrCreate(this._src(audioId));
    }
    resume() {
        // Howler creates its context lazily, so this is a no-op before the first
        // sound is constructed — which is fine: a context that doesn't exist yet
        // will be created inside a gesture-initiated play() anyway.
        const ctx = Howler.ctx;
        if (ctx?.state === 'suspended')
            void ctx.resume();
    }
    play(audioId, volume, options = {}) {
        const src = this._src(audioId);
        const sound = getOrCreate(src);
        const fadeInMs = options.fadeInMs ?? 0;
        // Start silent when fading in, so the ramp begins where the ear expects it.
        // Set BEFORE `play()`: Howler reads the Howl's volume when a playback id is
        // created, so setting it afterwards would let one frame through at full
        // level — an audible click, and exactly the artefact a fade exists to avoid.
        sound.volume(fadeInMs > 0 ? 0 : volume);
        const id = sound.play();
        if (options.loop) {
            // Loop is set per playback id rather than on the Howl: the same cached
            // Howl may later be played as a one-shot, and a Howl-level loop sticks.
            sound.loop(true, id);
            _looping.set(src, id);
        }
        // Per playback id, like the fade in `stop()`, so a ramp cannot be applied to
        // an unrelated voice sharing the same Howl.
        if (fadeInMs <= 0)
            return;
        if (sound.state() === 'loaded') {
            sound.fade(0, volume, fadeInMs, id);
            return;
        }
        // Module 150 — never hand Howler a fade while the file is still loading.
        // Measured: Howler queues it, replays it on load (twice, in the trace), and
        // a stop() queued behind it then leaves a fade interval that nothing ever
        // clears — a timer setting the volume and emitting 'fade' every few ms for
        // the rest of the session. So the fade waits for the load and for Howler's
        // own queue (play, loop) to drain, and runs only if this playback is still
        // the one wanted.
        sound.once('load', () => {
            setTimeout(() => {
                if (options.loop && _looping.get(src) !== id)
                    return;
                if (sound.state() === 'loaded' && sound.playing(id))
                    sound.fade(0, volume, fadeInMs, id);
            }, 0);
        });
    }
    setVolume(audioId, volume) {
        // `.volume(vol)` with no playback id sets the Howl's own default AND, per
        // Howler's own implementation, walks every currently active instance and
        // updates it too — which is exactly "reach whatever is sounding right
        // now", for free.
        _cache.get(this._src(audioId))?.volume(volume);
    }
    stop(audioId, fadeMs = 0) {
        const src = this._src(audioId);
        const sound = _cache.get(src);
        if (!sound)
            return;
        const id = _looping.get(src);
        if (id === undefined) {
            // Not looping (never started, or already stopped) — nothing to fade.
            sound.stop();
            return;
        }
        _looping.delete(src);
        // Module 150 — a track stopped while its file is still loading has not
        // made a sound yet: stop it without a fade (a queued fade is what left
        // Howler's interval running, see `play()`), and release it once it loads.
        if (sound.state() !== 'loaded') {
            sound.stop(id);
            sound.once('load', () => releaseIfIdle(src, sound));
            return;
        }
        // Module 72 — fade from THIS playback's volume, never the Howl's default.
        // `play()` sets the default to 0 for every fade-in, so `sound.volume()`
        // read 0 for every music track since Module 64: the fade ran 0 → 0, which
        // Howler never completes — the track cut to silence instead of fading, the
        // `'fade'` event never came, and the playback was never stopped. Every
        // level change left another silent loop running, and a Music slider move
        // (which walks every live instance) brought them all back at once.
        const from = sound.volume(id);
        if (fadeMs <= 0 || !(from > 0)) {
            sound.stop(id);
            releaseIfIdle(src, sound);
            return;
        }
        // Stop only when THIS playback's fade completes, so the listener can't be
        // fired by an unrelated fade on another id.
        sound.fade(from, 0, fadeMs, id);
        sound.once('fade', () => {
            sound.stop(id);
            releaseIfIdle(src, sound);
        }, id);
    }
}
/**
 * Module 150 (WP-150.2) — unload a long loop once nothing is playing it, so
 * its decoded buffer can be collected. Left alone if it was started again
 * while it faded out (it is looping again under a new id), if any other
 * playback of it is still sounding, or if the cache already holds a newer Howl.
 *
 * DEFERRED to the next task, never run inside a Howler callback. Measured: when
 * a track was stopped while its file was still loading, Howler replayed the
 * queued fade-out, which first CANCELS the queued fade-in, and Howler emits
 * 'fade' on a cancel too. Our `once('fade')` fired in the middle of Howler's
 * own `fade()`, the Howl was unloaded there, and Howler then started the
 * fade-out's interval on the dead Howl: a timer nothing could clear, queueing
 * a `volume()` call every few ms (~0.4 MB of heap a puzzle). `stop()` before
 * `unload()` cancels every fade interval still attached to the Howl.
 */
function releaseIfIdle(src, sound) {
    setTimeout(() => {
        if (_cache.get(src) !== sound || sound.state() === 'unloaded')
            return;
        if (_looping.has(src))
            return;
        if (!(sound.duration() >= RELEASE_LOOP_MIN_SECONDS))
            return;
        if (sound.playing())
            return;
        sound.stop();
        sound.unload();
        _cache.delete(src);
    }, 0);
}
